import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

// import logo from './logo.svg';
import "./App.css";

import NoSsr from "@mui/material/NoSsr";
import { ThemeProvider, Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import appTema from "src/servicos/mui/Theme";
import AppContexto from "./servicos/app-contexto/AppContexto";
import AppRouterConfig from "./AppRouterConfig";
import Auth0Service from "./servicos/auth0/Auth0Service";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { DemoModeProvider } from "./contexts/DemoModeContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { DemoBanner } from "./components/DemoBanner";
import { AssurerBanner } from "./components/AssurerBanner";
import { TourProvider } from "./components/tour/TourProvider";

// Template Import
import AppTopo from "./componentes/template/AppTopo";
import AppFooter from "./componentes/template/AppFooter";
import Usuario from "./servicos/usuario/Usuario";

// Page Import — eager (auth + error pages, needed for first paint / error states)
// import PricingPage from "./pages/auth/PricingPage"; // File doesn't exist - using ModernPricing instead
import ModernPricing from "./pages/auth/ModernPricing";
import Signup from "./pages/auth/Signup";
import SignupSuccess from "./pages/auth/SignupSuccess";
import LandingPage from "./pages/auth/LandingPage";
import PaymentPage from "./pages/auth/PaymentPage";
import PaymentSuccess from "./pages/auth/PaymentSuccess";
import Page404 from "./pages/404";
import FeatureGate from "./components/FeatureGate";
import DataPack from "./pages/userCompany/report/DataPack";
import CustomFactors from "./pages/userCompany/customFactors/CustomFactors";
import Baselines from "./pages/userCompany/report/Baselines";
import SpendBased from "./pages/userCompany/data/entitiy/spend/List";
import Scope3Categories from "./pages/userCompany/data/entitiy/spend/Categories";
import Assurance from "./pages/userCompany/assurance/Assurance";
import DataQuality from "./pages/userCompany/data-quality/DataQuality";
import S2Report from "./pages/userCompany/report/S2Report";
import NgerReport from "./pages/userCompany/report/NgerReport";
import DisclosureWorkspace from "./pages/userCompany/report/DisclosureWorkspace";
import Scope3Entries from "./pages/userCompany/data/scope3-entries/Scope3Entries";
import ClimateTargets from "./pages/userCompany/report/ClimateTargets";
import ClimateRisks from "./pages/userCompany/report/ClimateRisks";
import Portfolio from "./pages/userCompany/portfolio/Portfolio";
import ApiKeys from "./pages/userCompany/api-keys/ApiKeys";
import SupplierRequests from "./pages/userCompany/suppliers/SupplierRequests";
import Groups from "./pages/userCompany/groups/Groups";
import SupplierRequestLanding from "./pages/auth/SupplierRequestLanding";

// Page Import — lazy (authenticated app routes, code-split per route)
const Account = lazy(
	() => import("./pages/userCompany/account/Account/Account"),
);
const Membership = lazy(
	() => import("./pages/userCompany/account/Account/Membership"),
);
const CustomerPortal = lazy(
	() => import("./pages/CustomerPortal/CustomerPortal"),
);
const ChangePassword = lazy(
	() => import("./pages/userCompany/account/ChangePassword"),
);
const NewImportFileUpload = lazy(
	() => import("./pages/userCompany/data/NewImportFileUpload"),
);
const Dashboard = lazy(() => import("./pages/userCompany/dashboard/DashBoard"));
const FileLog = lazy(() => import("./pages/userCompany/data/FileLog"));
const ReportGenerateLog = lazy(
	() => import("./pages/userCompany/report/ReportGenerateLog"),
);
const Facilities = lazy(
	() => import("./pages/userCompany/data/entitiy/facility/List"),
);
const ReportBugs = lazy(() => import("./pages/ReportBugs"));

const Equipments = lazy(
	() => import("./pages/userCompany/data/entitiy/equipment/List"),
);
const Businesstravels = lazy(
	() => import("./pages/userCompany/data/entitiy/businesstravels/List"),
);

// Invitation System Pages
const Members = lazy(() =>
	import("./pages/Members").then((m) => ({ default: m.Members })),
);
const InvitePage = lazy(() => import("./pages/InvitePage"));
const ClaimCompany = lazy(() => import("./pages/ClaimCompany"));
const FuelCombustedPage = lazy(
	() => import("./pages/userCompany/data/entitiy/fuel-combusted/List"),
);
const ProfitPage = lazy(
	() => import("./pages/userCompany/data/entitiy/profit/List"),
);
const VehiclePage = lazy(
	() => import("./pages/userCompany/data/entitiy/vehicle/List"),
);
const FugitiveProcessesPage = lazy(
	() => import("./pages/userCompany/data/entitiy/fugitive-processes/List"),
);
const FugitiveGases = lazy(
	() => import("./pages/userCompany/data/entitiy/fugitive-gases/List"),
);
const ProcessCalcination = lazy(
	() => import("./pages/userCompany/data/entitiy/processes-calcination/List"),
);
const VehicleUsage = lazy(
	() => import("./pages/userCompany/data/entitiy/vehicle-usage/List"),
);
const TransportRoad = lazy(
	() => import("./pages/userCompany/data/entitiy/transport-road/List"),
);
const TransportSea = lazy(
	() => import("./pages/userCompany/data/entitiy/transport-sea/List"),
);

const OtherEmission = lazy(
	() => import("./pages/userCompany/data/entitiy/other-emissions/List"),
);

const Electricity = lazy(
	() => import("./pages/userCompany/data/entitiy/electricity/List"),
);
const CommutingPage = lazy(
	() => import("./pages/userCompany/data/entitiy/commuting/List"),
);
const BillUpload = lazy(() =>
	import("./pages/userCompany/data/BillUpload").then((m) => ({
		default: m.BillUpload,
	})),
);
const BillReview = lazy(() =>
	import("./pages/userCompany/data/BillUpload").then((m) => ({
		default: m.BillReview,
	})),
);

const ArtehDecarbonizationStrategies = lazy(
	() =>
		import("./pages/userCompany/macc/decarbonization-strategies/ArtehList"),
);
const CompanyDecarbonizationStrategies = lazy(
	() =>
		import("./pages/userCompany/macc/decarbonization-strategies/CompanyList"),
);

const ScenarioSimulation = lazy(
	() =>
		import("./pages/userCompany/macc/decarbonization-strategies/ScenarioSimulation"),
);

// Suspense fallback for lazy-loaded routes
const RouteFallback = () => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			minHeight: "60vh",
		}}
	>
		<CircularProgress />
	</Box>
);

// configura contexto da aplicacao
const appContexto = new AppContexto();
const ReactContext = React.createContext<AppContexto>(appContexto);
export { ReactContext };

declare global {
	interface Window {
		appContexto: AppContexto;
		appTheme: Theme;
	}
}

export default function App() {
	///
	/// States
	///
	const [user, setUser] = React.useState<Usuario | null>(null);
	const [processingInvitation, setProcessingInvitation] =
		React.useState(false);
	const auth0 = useAuth0();
	const { isAuthenticated, isLoading } = auth0;
	const [auth0Service, setAuth0Service] = React.useState<Auth0Service | null>(
		null,
	);
	const location = useLocation();

	// Define public routes that don't require authentication (inspired by Auth0 middleware pattern)
	const PUBLIC_ROUTES = [
		AppRouterConfig.PUBLIC.LANDING,
		AppRouterConfig.PUBLIC.MODERN_PRICING,
		AppRouterConfig.PUBLIC.SIGNUP,
		AppRouterConfig.PUBLIC.SIGNUP_SUCCESS,
		AppRouterConfig.PUBLIC.PAYMENT,
		AppRouterConfig.PUBLIC.PAYMENT_SUCCESS,
		AppRouterConfig.PUBLIC.INVITE,
		AppRouterConfig.PUBLIC.CLAIM,
		AppRouterConfig.PUBLIC.SUPPLIER_REQUEST,
		"/accept-invitation", // Backward compatibility for old invitation links
		"/subscription",
	];

	const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

	// Full-bleed auth screens render edge-to-edge: no AppTopo, no
	// DemoBanner, no maxWidth Container, no AppFooter. Keeps the
	// signup/payment design (left form column + right gradient panel)
	// flush against the viewport.
	const isFullBleedRoute =
		location.pathname.startsWith("/signup") ||
		location.pathname.startsWith("/payment");

	// Test environment gate: only administrator_arteh users can access
	const isTestDeployment =
		process.env.REACT_APP_IS_TEST_DEPLOYMENT === "true";
	const hasTestAccess = React.useMemo(() => {
		if (!isTestDeployment || !isAuthenticated || !auth0.user) return false;
		const roles =
			auth0.user["https://arteh.com.au/roles"] ||
			auth0.user["https://arteh.com.au//roles"] ||
			[];
		return Array.isArray(roles) && roles.includes("administrator_arteh");
	}, [isTestDeployment, isAuthenticated, auth0.user]);

	// Initialize Auth0 service
	React.useEffect(() => {
		const service = new Auth0Service(auth0);
		setAuth0Service(service);
		appContexto.setAuth0Service(service);
	}, [auth0]);

	// Check authentication when Auth0 state changes
	React.useEffect(() => {
		if (!isLoading && auth0Service) {
			checkUserAutenticado();
		}
		// TODO(lint): exhaustive-deps disabled — needs careful review of dep array
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, isAuthenticated, auth0Service]);

	const checkUserAutenticado = async () => {
		if (!isAuthenticated) {
			setUser(null);
			document.body.style.opacity = "1";
			return;
		}

		// Set user directly from Auth0 without calling Drupal
		const auth0User = auth0Service?.getUser();
		if (auth0User) {
			appContexto.usuario.Info = auth0User;
			appContexto.usuario.EhLogado = true;
			setUser(appContexto.usuario);
		} else {
			setUser(null);
		}

		// Check for pending invitation after authentication
		const pendingToken =
			sessionStorage.getItem("invitationToken") ||
			localStorage.getItem("pendingInvitation");

		if (pendingToken && auth0User && auth0Service) {
			// Use the new postLogin endpoint for invitation processing
			setProcessingInvitation(true);
			try {
				const accessToken = await auth0Service.getAccessToken();
				const response = await fetch(
					`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/auth/post-login`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${accessToken}`,
						},
						body: JSON.stringify({
							invitationToken: pendingToken,
						}),
					},
				);

				const data = await response.json();
				if (data.Ok && data.Data) {
					// Clear tokens after successful processing
					sessionStorage.removeItem("invitationToken");
					localStorage.removeItem("pendingInvitation");
					// Clear tour session to allow it to start fresh after invitation
					sessionStorage.removeItem("arteh_tour_started");

					// Handle redirect instructions from backend
					// Only redirect if we're not already on the target page
					if (
						data.Data.redirectTo &&
						location.pathname !== data.Data.redirectTo
					) {
						window.location.href = data.Data.redirectTo;
						return; // Exit early if redirecting
					}
				}
			} catch (error) {
				console.error(
					"Failed to process invitation via postLogin:",
					error,
				);
			} finally {
				setProcessingInvitation(false);
			}
		}

		document.body.style.opacity = "1";
	};

	///
	/// App Contexto e Theme
	///

	// Debug
	window["appContexto"] = appContexto;
	window["appTheme"] = appTema;

	// CSS
	const StyledContainer = styled(Container)(({ theme: _theme }) => ({
		paddingTop: 32,
		flex: "1 0 auto",
	}));

	// Show loading state while processing invitation
	if (processingInvitation) {
		return (
			<ThemeProvider theme={appTema}>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
				>
					<CircularProgress />
					<Typography sx={{ ml: 2 }}>
						Processing invitation...
					</Typography>
				</Box>
			</ThemeProvider>
		);
	}

	// Test environment gate: block non-admin users
	if (isTestDeployment && !isLoading) {
		if (!isAuthenticated) {
			return (
				<ThemeProvider theme={appTema}>
					<CssBaseline />
					<Box
						display="flex"
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						height="100vh"
						gap={2}
					>
						<Typography variant="h4">
							Restricted Test Environment
						</Typography>
						<Typography color="text.secondary">
							This environment is restricted to ARTEH
							administrators. Please log in to continue.
						</Typography>
						<Button
							variant="contained"
							onClick={() => auth0.loginWithRedirect()}
						>
							Log In
						</Button>
					</Box>
				</ThemeProvider>
			);
		}
		if (!hasTestAccess) {
			return (
				<ThemeProvider theme={appTema}>
					<CssBaseline />
					<Box
						display="flex"
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						height="100vh"
						gap={2}
					>
						<Typography variant="h4">Access Denied</Typography>
						<Typography color="text.secondary">
							This test environment is restricted to ARTEH
							administrators only.
						</Typography>
						<Button
							variant="outlined"
							onClick={() =>
								auth0.logout({
									logoutParams: {
										returnTo: window.location.origin,
									},
								})
							}
						>
							Log Out
						</Button>
					</Box>
				</ThemeProvider>
			);
		}
	}

	return (
		<ReactContext.Provider value={appContexto}>
			<CompanyProvider>
				<SubscriptionProvider>
					<DemoModeProvider>
						<TourProvider>
							<NoSsr>
								<ThemeProvider theme={appTema}>
									<CssBaseline />
									{/* Hide navigation on invitation + full-bleed auth screens for cleaner UX */}
									{!location.pathname.startsWith("/invite") &&
										!isFullBleedRoute && <AppTopo />}
									{!location.pathname.startsWith("/invite") &&
										!isFullBleedRoute && (
											<>
												<DemoBanner />
												<AssurerBanner />
											</>
										)}
									<StyledContainer
										maxWidth={
											isFullBleedRoute ? false : "lg"
										}
										disableGutters={isFullBleedRoute}
										sx={
											isFullBleedRoute
												? {
														paddingTop:
															"0 !important",
													}
												: undefined
										}
									>
										<div className="App">
											{
												///
												/// ROTAS
												///
											}
											{isLoading ? (
												// Show loading spinner while Auth0 is checking authentication
												<Box
													sx={{
														display: "flex",
														justifyContent:
															"center",
														alignItems: "center",
														minHeight: "60vh",
													}}
												>
													<CircularProgress />
												</Box>
											) : isPublicRoute ? (
												<Suspense
													fallback={<RouteFallback />}
												>
													<Routes>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.LANDING
															}
															element={
																<LandingPage />
															}
														/>
														<Route
															path="/subscription"
															element={
																<ModernPricing />
															}
														/>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.MODERN_PRICING
															}
															element={
																<ModernPricing />
															}
														/>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.SIGNUP
															}
															element={<Signup />}
														/>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.SIGNUP_SUCCESS
															}
															element={
																<SignupSuccess />
															}
														/>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.PAYMENT
															}
															element={
																<PaymentPage />
															}
														/>
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.PAYMENT_SUCCESS
															}
															element={
																<PaymentSuccess />
															}
														/>
														{/* Invite Page (public route) */}
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.INVITE
															}
															element={
																<InvitePage />
															}
														/>
														{/* Claim Company (public route — migration from old app) */}
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.CLAIM
															}
															element={
																<ClaimCompany />
															}
														/>
														{/* Supplier request landing (public — P4 flywheel) */}
														<Route
															path={
																AppRouterConfig
																	.PUBLIC
																	.SUPPLIER_REQUEST
															}
															element={
																<SupplierRequestLanding />
															}
														/>
														{/* Backward compatibility redirect for old invitation links */}
														<Route
															path="/accept-invitation"
															element={
																<Navigate
																	to={`/invite${location.search}`}
																	replace
																/>
															}
														/>
													</Routes>
												</Suspense>
											) : !isAuthenticated ? (
												<Navigate to="/" replace />
											) : (() => {
													// Check if user has a company ID in their metadata
													const userMetadata =
														auth0.user?.[
															"https://arteh.com.au/user_metadata"
														];
													const appMetadata =
														auth0.user?.[
															"https://arteh.com.au/app_metadata"
														];
													// Check if user has companies in the new metadata structure
													const hasCompanies =
														appMetadata?.companies &&
														appMetadata.companies
															.length > 0;
													const _hasActiveCompanyId =
														userMetadata?.activeCompanyId;

													// Check if user should skip onboarding due to invitation
													const urlParams =
														new URLSearchParams(
															location.search,
														);
													const hasInvitationInUrl =
														urlParams.get(
															"process_invitation",
														);
													const skipOnboarding =
														userMetadata?.skipOnboarding ||
														userMetadata?.invitationAccepted ||
														sessionStorage.getItem(
															"invitationToken",
														) ||
														hasInvitationInUrl ||
														processingInvitation;

													// If no companies, check invitation status before redirecting
													if (
														!hasCompanies &&
														location.pathname !==
															AppRouterConfig
																.PUBLIC.SIGNUP
													) {
														if (skipOnboarding) {
															// Invited user without company yet - let them complete invitation
															return true; // Allow current route
														}
														return false; // Redirect to signup
													}

													// All authenticated users have access (they're members by default)
													// Just check if they're logged in or have companies
													return user || hasCompanies;
											  })() ? (
												<Suspense
													fallback={<RouteFallback />}
												>
													<Routes>
														{/* Dashboard */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.DASHBOARD
																	.INDEX
															}
															element={
																<Dashboard />
															}
														/>
														{/* Reports*/}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.INDEX
															}
															element={
																<FeatureGate feature="canGenerateReports">
																	<ReportGenerateLog />
																</FeatureGate>
															}
														/>
														{/* Scope 3 categories */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.SCOPE3CATEGORIES
															}
															element={
																<FeatureGate feature="scope3Categories">
																	<Scope3Categories />
																</FeatureGate>
															}
														/>
														{/* Spend-based Scope 3 */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.SPENDBASED
															}
															element={
																<FeatureGate feature="spendBasedScope3">
																	<SpendBased />
																</FeatureGate>
															}
														/>
														{/* Custom emission factors */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.CUSTOMFACTORS
															}
															element={
																<FeatureGate feature="customEmissionFactors">
																	<CustomFactors />
																</FeatureGate>
															}
														/>
														{/* Supplier requests */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.SUPPLIER_REQUESTS
															}
															element={
																<FeatureGate feature="supplierRequests">
																	<SupplierRequests />
																</FeatureGate>
															}
														/>
														{/* Group consolidation */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.GROUPS
															}
															element={
																<FeatureGate feature="multiEntityConsolidation">
																	<Groups />
																</FeatureGate>
															}
														/>
														{/* Assurance */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.ASSURANCE
															}
															element={
																<FeatureGate feature="assuranceRegisters">
																	<Assurance />
																</FeatureGate>
															}
														/>
														{/* Data quality (backlog Q-01) */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.DATA_QUALITY
															}
															element={
																<FeatureGate feature="dataQualityScore">
																	<DataQuality />
																</FeatureGate>
															}
														/>
														{/* AASB S2 report */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.S2REPORT
															}
															element={
																<FeatureGate feature="s2Report">
																	<S2Report />
																</FeatureGate>
															}
														/>
														{/* NGER readiness report */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.NGER_REPORT
															}
															element={
																<FeatureGate feature="dataQualityScore">
																	<NgerReport />
																</FeatureGate>
															}
														/>
														{/* Scope 3 entries (S-02/03/04/05) */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.SCOPE3ENTRIES
															}
															element={
																<FeatureGate feature="spendBasedScope3">
																	<Scope3Entries />
																</FeatureGate>
															}
														/>
														{/* API keys (M-04) */}
														<Route
															path={
																AppRouterConfig
																	.USER
																	.API_KEYS
															}
															element={
																<FeatureGate feature="hasApiAccess">
																	<ApiKeys />
																</FeatureGate>
															}
														/>
														{/* Portfolio console (M-02) */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.PORTFOLIO
															}
															element={<Portfolio />}
														/>
														{/* Risk register + scenarios (C-01/C-02) */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.CLIMATE_RISKS
															}
															element={
																<FeatureGate feature="s2Report">
																	<ClimateRisks />
																</FeatureGate>
															}
														/>
														{/* Climate targets (T-01/T-02) */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.CLIMATE_TARGETS
															}
															element={
																<FeatureGate feature="s2Report">
																	<ClimateTargets />
																</FeatureGate>
															}
														/>
														{/* Disclosure workspace (A-02) */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.DISCLOSURE_WORKSPACE
															}
															element={
																<FeatureGate feature="s2Report">
																	<DisclosureWorkspace />
																</FeatureGate>
															}
														/>
														{/* Baselines */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.BASELINES
															}
															element={
																<FeatureGate feature="baselining">
																	<Baselines />
																</FeatureGate>
															}
														/>
														{/* Customer data pack */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.REPORT
																	.DATA_PACK
															}
															element={
																<FeatureGate feature="dataPackExport">
																	<DataPack />
																</FeatureGate>
															}
														/>
														{/* File upload */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.DATA
																	.FILEUPLOAD
															}
															element={
																<NewImportFileUpload />
															}
														/>
														{/* All files */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.DATA
																	.FILELOG
															}
															element={
																<FileLog />
															}
														/>

														{/* Management */}
														{/* Facilities */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.FACILITIES
															}
															element={
																<Facilities />
															}
														/>
														{/* Profit */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.PROFIT
															}
															element={
																<ProfitPage />
															}
														/>
														{/* Vehicle Page */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.VEHICLE
															}
															element={
																<VehiclePage />
															}
														/>
														{/* Other Emissions */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.OTHEREMISSIONS +
																"/:scopeID"
															}
															element={
																<OtherEmission />
															}
														/>

														{/* Scope 1 */}
														{/* Equipment */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.EQUIPMENT
															}
															element={
																<Equipments />
															}
														/>
														{/* Fuel Combusted */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.FUELCOMBUSTED
															}
															element={
																<FuelCombustedPage />
															}
														/>
														{/* Fugitive Processes */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.FUGITIVEPROCESS
															}
															element={
																<FugitiveProcessesPage />
															}
														/>
														{/* Process Calcination */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.PROCESSESCALCINATION
															}
															element={
																<ProcessCalcination />
															}
														/>
														{/* Vehicle Usage */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.VEHICLEUSAGE
															}
															element={
																<VehicleUsage />
															}
														/>

														{/* Scope 2 */}
														{/* Electricity */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.ELECTRICITY
															}
															element={
																<Electricity />
															}
														/>
														{/* Bill Upload */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.DATA
																	.BILLUPLOAD
															}
															element={
																<BillUpload />
															}
														/>
														{/* Bill Review */}
														<Route
															path={
																AppRouterConfig
																	.USERCOMPANY
																	.DATA
																	.BILLREVIEW
															}
															element={
																<BillReview />
															}
														/>

														{/* Scope 3 */}
														{/* Busssines Travels */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.BUSINESSTRAVELS
															}
															element={
																<FeatureGate feature="scope3ActivityModules">
																	<Businesstravels />
																</FeatureGate>
															}
														/>
														{/* Commuting */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.COMMUTING
															}
															element={
																<FeatureGate feature="scope3ActivityModules">
																	<CommutingPage />
																</FeatureGate>
															}
														/>
														{/* Fugitive from Gas Leakage */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.FUGITIVEGASES
															}
															element={
																<FugitiveGases />
															}
														/>
														{/* Transport Road */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.TRANSPORTROAD
															}
															element={
																<FeatureGate feature="scope3ActivityModules">
																	<TransportRoad />
																</FeatureGate>
															}
														/>
														{/* Transport Sea */}
														<Route
															path={
																AppRouterConfig
																	.MANAGEMENT
																	.TRANSPORTSEA
															}
															element={
																<FeatureGate feature="scope3ActivityModules">
																	<TransportSea />
																</FeatureGate>
															}
														/>

														{/* Account */}
														{/* My Acount */}
														<Route
															path={
																AppRouterConfig
																	.USER
																	.ACCOUNT
															}
															element={
																<Account />
															}
														/>
														{/* Membership */}
														<Route
															path={
																AppRouterConfig
																	.USER
																	.MEMBERSHIP
															}
															element={
																<Membership />
															}
														/>
														{/* Customer Portal */}
														<Route
															path={
																AppRouterConfig
																	.USER
																	.CUSTOMER_PORTAL
															}
															element={
																<CustomerPortal />
															}
														/>
														{/* Change Password */}
														<Route
															path={
																AppRouterConfig
																	.USER
																	.CHANGEPASSWORD
															}
															element={
																<ChangePassword />
															}
														/>
														<Route
															path={
																AppRouterConfig
																	.USER
																	.REPORT_BUG
															}
															element={
																<ReportBugs />
															}
														/>

														{/* Team Members Management */}
														<Route
															path="/members"
															element={
																<Members />
															}
														/>

														{/* MACC */}
														{/* Arteh Decarbonization Strategies */}
														<Route
															path={
																AppRouterConfig
																	.MACC
																	.ARTEHDECARBONIZATIONSTRATEGIES
															}
															element={
																<FeatureGate feature="canRunSimulations">
																	<ArtehDecarbonizationStrategies />
																</FeatureGate>
															}
														/>
														{/* Company Decarbonization Strategies */}
														<Route
															path={
																AppRouterConfig
																	.MACC
																	.COMPANYDECARBONIZATIONSTRATEGIES
															}
															element={
																<FeatureGate feature="canRunSimulations">
																	<CompanyDecarbonizationStrategies />
																</FeatureGate>
															}
														/>
														{/* ScenarioSimulation Strategies */}
														<Route
															path={
																AppRouterConfig
																	.MACC
																	.SCENARIOSIMULATION
															}
															element={
																<FeatureGate feature="canRunSimulations">
																	<ScenarioSimulation />
																</FeatureGate>
															}
														/>

														{/* Index */}
														<Route
															path={
																AppRouterConfig.INDEX
															}
															element={
																<Dashboard />
															}
														/>
														{/* 404 */}
														<Route
															path="*"
															element={
																<Page404 />
															}
														/>
													</Routes>
												</Suspense>
											) : (
												// If authenticated but no company, redirect to signup
												<Navigate
													to={
														AppRouterConfig.PUBLIC
															.SIGNUP
													}
													replace
												/>
											)}
										</div>
									</StyledContainer>

									{!isFullBleedRoute && <AppFooter />}
								</ThemeProvider>
							</NoSsr>
						</TourProvider>
					</DemoModeProvider>
				</SubscriptionProvider>
			</CompanyProvider>
		</ReactContext.Provider>
	);
}
